import React, { FC } from "react";
import { FieldError, UseFormReturn } from "react-hook-form";
import DatePickerControl from "components/form/DatePickerControl";
import TextFieldControl from "components/form/TextFieldControl";
import AutocompleteControl from "components/form/AutocompleteControl";
import { FilterField } from "layout/TableConstructor/types";
import { FormHelperText, Stack, Typography } from "@mui/material";
import dayjs from "dayjs";
import typography from "constants/typography.constants";

export enum InputType {
  DATE = "DATE",
  TEXT = "TEXT",
  AUTOCOMPLETE = "AUTOCOMPLETE",
}

export const INPUT_DEFAULT_VALUE = {
  [InputType.DATE]: null,
  [InputType.TEXT]: "",
  [InputType.AUTOCOMPLETE]: null,
};
type InputsGeneratorProps = {
  input: FilterField<object>;
  form: UseFormReturn<any>;
};

const GeneratedInput: FC<InputsGeneratorProps> = ({ input, form, ...rest }) => {
  const { type, rules } = input;
  const {
    formState: { errors },
    setValue,
  } = form;

  const onChangeNumber = (name: string, value: number) => {
    let newValue = value;
    if (+value < 1) newValue = 1;
    setValue(name, newValue);
  };

  switch (type) {
    case InputType.DATE: {
      return (
        <DatePickerControl
          rules={{
            validate: (value: Date) => (value ? dayjs(value).isValid() : true),
          }}
          maxDate={new Date()}
          {...{ ...input, ...rest }}
        />
      );
    }
    case InputType.TEXT: {
      // eslint-disable-next-line @typescript-eslint/no-shadow
      const { type, inputType, isRange, ...inputRest } = input;
      const baseProps = {
        size: "small",
        type: input.inputType,
      } as const;
      const minRule = {
        value: 0,
        message: typography.rules.min(0),
      };

      if (isRange && inputType === "number") {
        const error = errors[inputRest.name] as { from: FieldError | undefined; to: FieldError | undefined };
        const { label } = inputRest;

        const rangeBaseValidate = (currentValue: string, pairValue: string) => {
          const isCurrentValueEmpty = currentValue === "";
          const isPairValueEmpty = pairValue === "";

          if ((isCurrentValueEmpty && !isPairValueEmpty) || (isPairValueEmpty && !isCurrentValueEmpty)) {
            return "Оба поля должны быть заполнены";
          }

          return true;
        };

        return (
          <Stack>
            {label && (
              <Typography
                variant='body14rg'
                sx={{ color: "base.500", marginBottom: 0.5 }}>
                {label}
              </Typography>
            )}
            <Stack
              sx={{ width: "100%", "& > div": { flex: 1 } }}
              alignItems='flex-end'
              direction='row'
              spacing={0.5}>
              <TextFieldControl
                {...baseProps}
                startIcon='от'
                placeholder=''
                rules={{
                  validate: (from: string, formValue) => {
                    const { to } = formValue[inputRest.name] as { to: string };

                    if (from && to && +from > +to) {
                      return "Значение не может быть больше верхней границы";
                    }

                    return rangeBaseValidate(from, to);
                  },
                  min: minRule,
                  ...rules,
                }}
                name={`${inputRest.name}.from`}
                hideHelperText
                onNumberValueChange={(value) => onChangeNumber(`${inputRest.name}.from`, value)}
                {...rest}
              />
              <TextFieldControl
                {...baseProps}
                name={`${inputRest.name}.to`}
                startIcon='до'
                hideHelperText
                onNumberValueChange={(value) => onChangeNumber(`${inputRest.name}.to`, value)}
                rules={{
                  validate: (to, formValue) => {
                    const { from } = formValue[inputRest.name] as { from: string };
                    return rangeBaseValidate(to, from);
                  },
                  min: minRule,
                  ...rules,
                }}
                {...rest}
              />
            </Stack>
            {error ? (
              <FormHelperText sx={{ color: "error.contrastText" }}>
                {error.from?.message || error.to?.message}
              </FormHelperText>
            ) : null}
          </Stack>
        );
      }
      // Для предотвращение warning в консоли
      const { additionalFilter, customFilter, ...filteredInputRest } = inputRest;

      if (inputType === "number") {
        return (
          <TextFieldControl
            {...baseProps}
            onNumberValueChange={(value) => onChangeNumber(inputRest.name, value)}
            rules={{ min: minRule }}
            {...{ ...filteredInputRest, ...rest }}
          />
        );
      }

      return (
        <TextFieldControl
          {...baseProps}
          {...{ ...filteredInputRest, ...rest }}
        />
      );
    }
    case InputType.AUTOCOMPLETE: {
      return <AutocompleteControl {...{ ...input, ...rest }} />;
    }

    default:
      return null;
  }
};

export default GeneratedInput;
